import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './HeroCarousel.css';

const heroImages = [
  '/images/hero1.jpg',
  '/images/hero2.jpg',
  '/images/hero3.jpg'
];

const HeroCarousel = () => {
  const [displayText, setDisplayText] = useState('');
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const fullText = 'Where Smart Tech Meets Livestock Care';
  const typingSpeed = 40; // milliseconds per character

  useEffect(() => {
    // Preload images and set first one as loaded
    const preloadImages = () => {
      let loadedCount = 0;
      heroImages.forEach((src) => {
        const img = new Image();
        img.onload = () => {
          loadedCount++;
          if (loadedCount === heroImages.length) {
            setImagesLoaded(true);
          }
        };
        img.src = src;
      });
    };

    preloadImages();
  }, []); // Separate useEffect for image preloading

  useEffect(() => {
    // Hero image rotation - only start after images are loaded
    const imageInterval = setInterval(() => {
      if (imagesLoaded) {
        setCurrentHeroImage((prev) => (prev + 1) % heroImages.length);
      }
    }, 5000); // Change every 5 seconds

    return () => clearInterval(imageInterval);
  }, [imagesLoaded]); // Include imagesLoaded dependency

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, i) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            entry.target.classList.add('visible');
          }, i * 80);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15 });

    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));

    // Typewriter effect
    let currentIndex = 0;
    const typeWriter = () => {
      if (currentIndex < fullText.length) {
        let currentText = fullText.slice(0, currentIndex + 1);
        
        // Add styling to "Smart Tech" part
        currentText = currentText.replace('Smart Tech', '<em>Smart Tech</em>');
        
        setDisplayText(currentText);
        currentIndex++;
        setTimeout(typeWriter, typingSpeed);
      }
    };

    // Start typewriter after a short delay
    const typewriterTimeout = setTimeout(typeWriter, 500);

    // Show scroll hint after 0.5 seconds
    const scrollHintTimeout = setTimeout(() => {
      setShowScrollHint(true);
    }, 500);

    // Scroll hint visibility
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowScrollHint(false); // Hide scroll hint when scrolling
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup function
    return () => {
      observer.disconnect();
      clearTimeout(typewriterTimeout);
      clearTimeout(scrollHintTimeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section id="home" className="hero">
      {/* Hero Background Images */}
      <div className="hero-background-images">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`hero-bg-image ${(index === currentHeroImage && imagesLoaded) || (index === 0 && !imagesLoaded) ? 'active' : ''}`}
            style={{ 
              backgroundImage: `url(${image})`
            }}
          />
        ))}
        
        {/* Main circular mask */}
        <div className="circular-mask"></div>
        
        {/* Circular layers as actual elements */}
        <div className="circular-layer-1"></div>
        <div className="circular-layer-2"></div>
        <div className="circular-layer-3"></div>
      </div>

      <div className="hero-content">
        <div className="hero-badge">STARTUP INDIA RECOGNIZED</div>
        <h1 dangerouslySetInnerHTML={{ __html: displayText + '<span class="typing-cursor">|</span>' }}>
        </h1>
        <p className="hero-sub">
          Adept Knowledge Technologies delivers cutting - edge, innovation - driven solutions to real-world challenges - from autonomous sensing systems and AI to transforming India's dairy industry, one healthy animal at a time.
        </p>
        <div className="hero-actions">
          <Link to="/about" className="btn btn-primary">
            Explore Our Work
            <span className="btn-arrow">→</span>
          </Link>
          <Link to="/contact" className="btn btn-secondary">Contact Us</Link>
        </div>
      </div>

      {/* Scroll Hint - appears after typewriter */}
      <div className={`scroll-hint ${showScrollHint ? 'show' : ''}`}>
        <div className="scroll-hint-text">Scroll to see the Research Exploration Timeline</div>
        <div className="scroll-hint-arrow">
          <i className="fa-solid fa-angle-down"></i>
        </div>
      </div>

      {/* Decorative visual */}
      <div className="hero-visual">
        <div className="visual-ring"></div>
        <div className="visual-ring"></div>
        <div className="visual-ring"></div>
        <div className="visual-center">
          <img 
            src="https://images.unsplash.com/photo-1665936091620-bc6c8bec50e3?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D" 
            alt="Cow with Baby Cow" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              borderRadius: '50%'
            }}
          />
        </div>
        <div className="orbit-dot">🔬</div>
        <div className="orbit-dot">📡</div>
        <div className="orbit-dot">💻</div>
        <div className="orbit-dot">🧠</div>
      </div>
    </section>
  );
};

export default HeroCarousel;